import { AsyncPipe, DOCUMENT, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ContentChild,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  signal,
  TemplateRef,
  viewChild,
  ViewChild
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NgbTooltip, Placement, PlacementArray } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ButtonComponent } from '../button';
import { LoaderComponent } from '../loader/loader.component';
import { HEADER_HEIGHT } from '../utils/constants';
import { SidepanelEventsService } from './sidepanel-events.service';

export type modalText = { title?: string; text?: string; textInvalid?: string; save?: string; back?: string; cancel?: string };
export enum PanelState {
  Default = 'Default',
  Incomplete = 'Incomplete',
  Precolumn = 'Precolumn',
  Fullscreen = 'Fullscreen'
}
enum PanelStateIcon {
  Default = 'fa fa-compress',
  Incomplete = 'fa fa-expand',
  Precolumn = 'fa fa-th-list',
  Fullscreen = 'fa fa-expand fa-expand--horizontal'
}
const PANEL_STATE_ICON_MAP: Record<PanelState, string> = {
  [PanelState.Default]: PanelStateIcon.Default,
  [PanelState.Incomplete]: PanelStateIcon.Incomplete,
  [PanelState.Precolumn]: PanelStateIcon.Precolumn,
  [PanelState.Fullscreen]: PanelStateIcon.Fullscreen
};

/**
 * A component that provides a sidebar
 */
@Component({
  selector: 'app-sidepanel,mbs-sidepanel',
  templateUrl: './sidepanel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, ButtonComponent, LoaderComponent, NgbTooltip, NgClass, NgTemplateOutlet],
  host: {
    '(window:keydown.esc)': 'handleClose()'
  }
})
export class SidepanelComponent implements OnInit, OnDestroy, OnChanges {
  private cd = inject(ChangeDetectorRef);
  private document = inject<Document>(DOCUMENT);
  private renderer = inject(Renderer2);
  private sidepanelEvents = inject(SidepanelEventsService);

  protected readonly headerElement = viewChild<ElementRef<HTMLElement>>('headerEl');
  @ViewChild('sidePanel', { static: false }) sidePanel: ElementRef<HTMLElement>;

  @ContentChild('footerTemplate', { static: true }) footerTemplateContent: TemplateRef<any>;

  /**
   * Disables the save action/button in the footer
   */
  public readonly disabledSave = input(false);
  public readonly disabledSave$ = toObservable(this.disabledSave);

  /**
   * Array of PanelStates that should be skipped during cycle toggling
   */
  public readonly skipStates = input<PanelState[]>([]);

  /**
   * Determines whether the side panel includes an additional pre-column layout
   */
  public readonly multiState = input<boolean>(false);

  /**
   * Custom text for the modal
   */
  public readonly modalText = input<modalText | null>(null);

  /**
   * A uniq id in the page scope
   */
  @Input() id: string;

  /**
   * Which one side a panel appears
   * * Available values: `left | right`.
   * * default - `right`
   */
  public readonly side = input<string | null>(null);

  /**
   * Toggles the `-tabs` class that includes special styles for `ngb-tabset`
   * * use only if there is a `ngb-tabset` inside of `sidepanel`
   * * default - `false`
   */
  public readonly tabs = input<boolean | null>(null);

  /**
   * Toggles the `large mode`.
   * * If `true` panel becomes almost twice wider than usual.
   * * default - `false`
   */
  public readonly large = input<boolean | null>(null);

  @Input() headerClass: string;
  @Input() actionNames: Partial<{ delete: string; save: string; close: string }> = {};
  @Input() innerContentType: 'form' | 'inner' | 'tabs' | 'nowrap' = 'form';
  @Input() showFooter = true;
  @Input() deleteButtonTooltip: string;
  @Input() deleteButtonTooltipPosition: string | Placement | PlacementArray = 'top';
  @Input() saveButtonTooltip: string;
  @Input() saveButtonTooltipPosition: string | Placement | PlacementArray = 'top';
  @Input() disabledDelete: boolean;
  @Input() hideSave: boolean;
  @Input() showDelete = true;
  @Input() isCreate = true;

  @Input() showExpandedCross = false;
  @Input() showCloseIcon = true;

  @Input() loadingData = false;

  @Output() panelClosed = new EventEmitter();

  @Output('delete') deleteEvent = new EventEmitter();
  @Output('save') saveEvent = new EventEmitter();
  @Output() panelOpened = new EventEmitter();
  @Output() expandedChange = new EventEmitter<boolean>();
  @Output() panelStateChange = new EventEmitter<PanelState>();

  protected readonly panelState = signal<PanelState>(PanelState.Default);

  protected readonly nextPanelState = computed<PanelState>(() => {
    const states = this.availableStates();

    const currentIndex = states.indexOf(this.panelState());
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % states.length : 0;

    return states[nextIndex];
  });

  protected readonly headerIcon = computed(() => {
    if (!this.multiState()) {
      return this.isExpanded() ? PanelStateIcon.Default : PanelStateIcon.Incomplete;
    }

    const nextState = this.nextPanelState();
    if (nextState === PanelState.Incomplete) {
      return this.skipStates().includes(PanelState.Default) ? PanelStateIcon.Default : PanelStateIcon.Incomplete;
    }

    return PANEL_STATE_ICON_MAP[nextState];
  });

  protected readonly availableStates = computed<PanelState[]>(() => {
    const states = [PanelState.Default, PanelState.Incomplete, ...(this.multiState() ? [PanelState.Precolumn] : []), PanelState.Fullscreen];

    return states.filter((state) => !this.skipStates().includes(state));
  });

  protected readonly contentHeight = computed<string>(() => {
    const sidepanelHeaderHeight = this.headerElement()?.nativeElement?.offsetHeight ?? 56;

    return `calc(100vh - ${sidepanelHeaderHeight}px - var(--mbs-sidepanel-top-offset, ${HEADER_HEIGHT}px))`;
  });

  protected readonly sidebarSideClass = computed<Record<string, boolean>>(() => {
    const isExpanded = this.isExpanded();
    const isStateIncomplete = this.multiState() && this.panelState() === PanelState.Incomplete;
    const large = this.large();
    const side = this.side();
    const useXlg = isStateIncomplete && isExpanded;

    return {
      '-open': this.showPanel(),
      '-left': side === 'left',
      '-right': side === 'right' || !side,
      '-tabs': this.tabs(),
      '-lg': large && !useXlg,
      '-xlg': large && useXlg
    };
  });

  public readonly isExpanded = signal<boolean>(false);
  public PanelState = PanelState;

  protected readonly isFullWidth = computed<boolean>(() => this.isExpanded() && !this.sidebarSideClass()['-xlg']);

  public internalLoadingForSave = new BehaviorSubject<boolean>(false);
  public internalLoadingForDelete = new BehaviorSubject<boolean>(false);
  public loadingForSave$: Observable<boolean>;
  public loadingForDelete$: Observable<boolean>;
  private externalLoadingForSave = new BehaviorSubject<boolean>(false);
  private externalLoadingForDelete = new BehaviorSubject<boolean>(false);

  private readonly expandedClass = '-fullscreen';
  private readonly showPanel = signal<boolean>(false);

  constructor() {
    this.loadingForSave$ = combineLatest([this.internalLoadingForSave, this.externalLoadingForSave]).pipe(
      map(([internal, external]) => internal || external)
    );
    this.loadingForDelete$ = combineLatest([this.internalLoadingForDelete, this.externalLoadingForDelete]).pipe(
      map(([internal, external]) => internal || external)
    );

    effect(() => this.panelStateChange.emit(this.panelState()));
  }

  /**
   * Current panel visibility state
   * @param {boolean} state
   */
  get show(): boolean {
    return this.showPanel();
  }

  @Input() set show(state: boolean) {
    this.showPanel.set(state);
    this.cd.markForCheck();
  }

  /**
   * Additional custom loading state for 'Save' button.
   * Anyway internal loading state is managed by the component.
   * @param {boolean} value - The loading state of the save button.
   */
  @Input() set loadingForSave(value: boolean) {
    this.externalLoadingForSave.next(value);
  }

  /**
   * Additional custom loading state for 'Delete' button.
   * Anyway internal loading state is managed by the component.
   * @param {boolean} value - The loading state of the delete button.
   */
  @Input() set loadingForDelete(value: boolean) {
    this.externalLoadingForDelete.next(value);
  }

  @Input() set footerTemplate(value: TemplateRef<any>) {
    this.footerTemplateContent = value;
  }

  public handleClose(): void {
    this.applyExpandedStyle();
    this.panelClosed.emit();
  }

  ngOnInit(): void {
    if (!this.id) {
      throw Error('modal must have an id');
    }
  }

  ngOnChanges(): void {
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    [this.panelClosed, this.deleteEvent, this.saveEvent].forEach((event) => event.unsubscribe());
  }

  public open(): void {
    this.showPanel.set(true);
    this.applyPanelState(this.panelState());
    this.panelOpened.emit();
    this.cd.detectChanges();
  }

  public close(): void {
    this.showPanel.set(false);
    this.applyExpandedStyle();
    this.cd.detectChanges();
  }

  handleHeaderAction(): void {
    this.multiState() ? this.cyclePanelState() : this.toggleExpand();

    if (this.isFullWidth()) this.sidepanelEvents.onFullWidth.emit();
  }

  public setPanelState(state: PanelState): void {
    this.applyPanelState(state);
  }

  toggleExpand(): void {
    this.isExpanded.update((prev) => !prev);

    this.applyExpandedStyle();
    this.expandedChange.emit(this.isExpanded());
  }

  private applyExpandedStyle(): void {
    const isExpanded = this.isExpanded();
    const isExpandedPanelVisible = this.showPanel() && isExpanded;

    this.toggleRendererClass(this.document.body, 'panel-overflow-hidden', isExpandedPanelVisible);
    this.toggleRendererClass(this.sidePanel.nativeElement, this.expandedClass, isExpanded);
  }

  private toggleRendererClass(element: any, className: string, condition: boolean): void {
    condition ? this.renderer.addClass(element, className) : this.renderer.removeClass(element, className);
  }

  private cyclePanelState(): void {
    const states = this.availableStates();

    const currentIndex = states.indexOf(this.panelState());
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % states.length : 0;

    this.applyPanelState(states[nextIndex]);
  }

  private applyPanelState(state: PanelState): void {
    this.panelState.set(state);
    this.panelStateChange.emit(state);

    const shouldBeExpanded = state !== PanelState.Default;

    if (this.isExpanded() !== shouldBeExpanded) {
      this.isExpanded.set(shouldBeExpanded);

      this.applyExpandedStyle();
      this.expandedChange.emit(this.isExpanded());
    }
  }
}
